import AppShell from "@/components/AppShell";
import EventsCarousel from "@/components/EventsCarousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CalendarCheck,
  Wallet,
  Megaphone,
  Trophy,
  Users,
  Clock3,
  FileText,
  CalendarDays,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getCirculars,
  getHomepageContent,
  getNotificationsForUser,
  getLeaveRequests,
  getSessionUser,
  getStudentEditRequests,
  getTeachers,
  getFeeConfiguration,
} from "@/lib/eisStore";

const STUDENT_TILES = [
  { key: "attendance", label: "Attendance", Icon: CalendarCheck, color: "#FACC15", path: "/attendance" },
  { key: "fees", label: "Fees", Icon: Wallet, color: "#EC4899", path: "/fees" },
  { key: "circulars", label: "Circulars", Icon: Megaphone, color: "#22C55E", path: "/circulars" },
  { key: "result", label: "Result", Icon: Trophy, color: "#EF4444", path: "/result" },
];

const STUDENT_POLICY_DOCS = [
  {
    key: "refund-return-policy",
    title: "Refund / Return Policy",
    src: "/img/policies/refund-return-policy.pdf",
  },
  {
    key: "terms-of-service",
    title: "Terms of Service",
    src: "/img/policies/terms-of-service.pdf",
  },
  {
    key: "privacy-policy",
    title: "Privacy Policy",
    src: "/img/policies/privacy-policy.pdf",
  },
];

const TEACHER_TILES = [
  { key: "result", label: "Upload Result", Icon: FileText, color: "#EF4444", path: "/result" },
  { key: "timetable", label: "Timetable", Icon: CalendarDays, color: "#22C55E", path: "/timetable" },
];

const ADMIN_TILES = [
  { key: "notifications", label: "Notifications", Icon: CalendarCheck, color: "#6366F1", path: "/notifications" },
  { key: "circulars", label: "Circulars", Icon: Megaphone, color: "#22C55E", path: "/circulars" },
  { key: "student-data", label: "Student Data", Icon: Users, color: "#EF4444", path: "/student-data" },
  { key: "edit-homepage", label: "Edit Homepage", Icon: Clock3, color: "#F59E0B", path: "/edit-homepage" },
];

const MANAGEMENT_TILES = [
  { key: "notifications", label: "Notifications", Icon: CalendarCheck, color: "#6366F1", path: "/notifications" },
  { key: "circulars", label: "Circulars", Icon: Megaphone, color: "#22C55E", path: "/circulars" },
  { key: "student-data", label: "Student Data", Icon: Users, color: "#EF4444", path: "/student-data" },
  { key: "teacher-data", label: "Teacher Data", Icon: Users, color: "#0EA5E9", path: "/teacher-data" },
  { key: "fees", label: "Fees", Icon: Wallet, color: "#EC4899", path: "/fees" },
];

function TeacherDashboard({ user, navigate }) {
  const homepage = getHomepageContent();
  const teacherTiles =
    user?.assignedClassKey
      ? TEACHER_TILES
      : TEACHER_TILES.filter((tile) => tile.key !== "attendance");

  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
            Welcome{user?.name ? `, ${user.name}` : ""} 👋
          </h2>
          <p className="text-slate-500 mt-1">
            Signed in as teacher. Manage your school tools from here.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {teacherTiles.map(({ key, label, Icon, color, path }) => (
            <button
              key={key}
              data-testid={`tile-${key}`}
              onClick={() => navigate(path)}
              className="nav-btn text-left rounded-2xl p-5 text-white shadow-md"
              style={{ backgroundColor: color }}
            >
              <Icon size={28} />
              <p className="text-lg font-bold mt-3">{label}</p>
              <p className="text-xs opacity-90 mt-1">Open workspace →</p>
            </button>
          ))}
        </div>

        <EventsCarousel />

        {homepage.achievements?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: "#0b1f7a" }}>
              ACHIEVEMENTS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {homepage.achievements.map((item) => (
                <div key={item.id} className="rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                  <img src={item.src} alt="Achievement" className="w-full h-56 object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="about-us-section">
          <h2
            className="text-3xl md:text-4xl font-extrabold mb-4"
            style={{ color: "#7C3AED" }}
          >
            ABOUT US
          </h2>
          <div
            data-testid="about-us-content"
            className="space-y-4 text-slate-700 text-sm md:text-base leading-relaxed"
          >
            {homepage.aboutUs.split("\n").map((line) => (
              <p key={line.slice(0, 20)} className={line ? "font-medium text-slate-800" : ""}>
                {line || "\u00a0"}
              </p>
            ))}
          </div>
        </section>

      </div>
    </AppShell>
  );
}

function StudentDashboard({ user, navigate }) {
  const homepage = getHomepageContent();
  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-10">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
            Welcome{user?.name ? `, ${user.name}` : ""} 👋
          </h2>
          <p className="text-slate-500 mt-1 capitalize">
            Signed in as {user?.role || "student"}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STUDENT_TILES.map(({ key, label, Icon, color, path }) => (
            <button
              key={key}
              data-testid={`tile-${key}`}
              onClick={() => navigate(path)}
              className="nav-btn text-left rounded-2xl p-5 text-white shadow-md"
              style={{ backgroundColor: color }}
            >
              <Icon size={28} />
              <p className="text-lg font-bold mt-3">{label}</p>
              <p className="text-xs opacity-90 mt-1">View details →</p>
            </button>
          ))}
        </div>

        <EventsCarousel />

        {homepage.achievements?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: "#0b1f7a" }}>
              ACHIEVEMENTS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {homepage.achievements.map((item) => (
                <div key={item.id} className="rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                  <img src={item.src} alt="Achievement" className="w-full h-56 object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="about-us-section">
          <h2
            className="text-3xl md:text-4xl font-extrabold mb-4"
            style={{ color: "#7C3AED" }}
          >
            ABOUT US
          </h2>
          <div
            data-testid="about-us-content"
            className="space-y-4 text-slate-700 text-sm md:text-base leading-relaxed"
          >
            {homepage.aboutUs.split("\n").map((line) => (
              <p key={line.slice(0, 20)} className={line ? "font-medium text-slate-800" : ""}>
                {line || "\u00a0"}
              </p>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: "#0b1f7a" }}>
            POLICY DOCUMENTS
          </h2>
          <div className="space-y-4">
            {STUDENT_POLICY_DOCS.map((doc) => (
              <Card key={doc.key} className="border-slate-200 shadow-sm">
                <CardContent className="py-5">
                  <a
                    href={doc.src}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 transition hover:border-[#0b1f7a] hover:bg-slate-50"
                    aria-label={`Open ${doc.title}`}
                  >
                    <span className="text-base md:text-lg font-semibold text-slate-800">
                      {doc.title}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#0b1f7a]">
                      Open PDF
                    </span>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

      </div>
    </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
        {label}
      </p>
      <p className="text-3xl font-bold text-slate-800 mt-2">{value}</p>
    </div>
  );
}

function AdminDashboard({ user, navigate }) {
  const notifications = getNotificationsForUser(user);
  const requests = getStudentEditRequests();
  const circulars = getCirculars();
  const homepage = getHomepageContent();

  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
            Welcome{user?.name ? `, ${user.name}` : ""} 👋
          </h2>
          <p className="text-slate-500 mt-1">
            Signed in as admin. Manage the full school experience here.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ADMIN_TILES.map(({ key, label, Icon, color, path }) => (
            <button
              key={key}
              data-testid={`tile-${key}`}
              onClick={() => navigate(path)}
              className="nav-btn text-left rounded-2xl p-5 text-white shadow-md"
              style={{ backgroundColor: color }}
            >
              <Icon size={28} />
              <p className="text-lg font-bold mt-3">{label}</p>
              <p className="text-xs opacity-90 mt-1">Open workspace →</p>
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>School Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat label="Notifications" value={notifications.length} />
            <Stat label="Edit Requests" value={requests.length} />
            <Stat label="Circulars" value={circulars.length} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Homepage Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <EventsCarousel />

            {homepage.achievements?.length > 0 && (
              <section className="space-y-4">
                <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: "#0b1f7a" }}>
                  ACHIEVEMENTS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {homepage.achievements.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-2xl overflow-hidden shadow-sm border border-slate-200"
                    >
                      <img src={item.src} alt="Achievement" className="w-full h-56 object-cover" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ color: "#7C3AED" }}>
                ABOUT US
              </h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                {homepage.aboutUs}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function ManagementDashboard({ user, navigate }) {
  const notifications = getNotificationsForUser(user);
  const leaveRequests = getLeaveRequests();
  const teachers = getTeachers();
  const feeConfig = getFeeConfiguration();
  const pendingLeaves = leaveRequests.filter((request) => request.status === "pending");
  const homepage = getHomepageContent();

  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
            Welcome{user?.name ? `, ${user.name}` : ""} 👋
          </h2>
          <p className="text-slate-500 mt-1">
            Signed in as management. Review leave requests and school updates here.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {MANAGEMENT_TILES.map(({ key, label, Icon, color, path }) => (
            <button
              key={key}
              data-testid={`tile-${key}`}
              onClick={() => navigate(path)}
              className="nav-btn text-left rounded-2xl p-5 text-white shadow-md"
              style={{ backgroundColor: color }}
            >
              <Icon size={28} />
              <p className="text-lg font-bold mt-3">{label}</p>
              <p className="text-xs opacity-90 mt-1">Open workspace →</p>
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Management Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat label="Notifications" value={notifications.length} />
            <Stat label="Leave Requests" value={leaveRequests.length} />
            <Stat label="Pending Leaves" value={pendingLeaves.length} />
            <Stat label="Teachers" value={teachers.length} />
            <Stat label="Fee Standards" value={Object.keys(feeConfig.byStandard || {}).length} />
          </CardContent>
        </Card>

        {pendingLeaves.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Pending Leave Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingLeaves.slice(0, 4).map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4"
                >
                  <div>
                    <p className="font-semibold text-slate-800">{request.teacherName}</p>
                    <p className="text-xs text-slate-500">
                      {request.fromDate} to {request.toDate}
                    </p>
                  </div>
                  <div className="text-right space-y-1">
                    <span className="block text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                      {request.leaveType || "Pending"}
                    </span>
                    <span className="block text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                      Pending
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Homepage Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
              {homepage.aboutUs}
            </p>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const user = getSessionUser();

  if (user?.role === "admin") {
    return <AdminDashboard user={user} navigate={navigate} />;
  }
  if (user?.role === "management") {
    return <ManagementDashboard user={user} navigate={navigate} />;
  }
  if (user?.role === "teacher") {
    return <TeacherDashboard user={user} navigate={navigate} />;
  }

  return <StudentDashboard user={user} navigate={navigate} />;
}
